import React from "react";
import moment from "moment";
import {
    BarChartOutlined,
    UnorderedListOutlined,
    FileDoneOutlined,
    TeamOutlined,
    DollarOutlined,
    TableOutlined,
    ToolOutlined,
    ExperimentOutlined,
    MedicineBoxOutlined,
    EnvironmentOutlined,
    AlertOutlined,
    CarOutlined,
    WalletOutlined,
} from "@ant-design/icons";
import { formatoMoneda, formatoPorcentaje, minutosATexto } from "../cmmsComun";

// Reportes del CMMS, tomados de los Excel con que hoy trabaja el cliente. Cada definicion
// dice que filtros pide, a que endpoint va y como convertir la respuesta en indicadores
// (tiles) y tablas. La MISMA estructura de tablas alimenta la pantalla y el Excel.
//
// Tabla: { titulo, hoja?, nota?, columnas: [{ titulo, tipo, ancho }], filas: [[valor...]], totales?: [valor|null...] }
// tipo: "texto" | "identificador" | "numero" | "moneda" | "porcentaje" (0..1) | "fecha" | "fechaHora"

const t = (titulo, tipo = "texto", ancho = 16) => ({ titulo, tipo, ancho });
const sum = (lista, f) => lista.reduce((a, x) => a + (Number(f(x)) || 0), 0);
const siNo = (v) => (v ? "Sí" : "No");
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const esteAnio = () => [moment().startOf("year"), moment()];
const esteMes = () => [moment().startOf("month"), moment()];

export const SECCIONES = ["Servicios", "Contratos", "Equipos", "Viajes"];

export const REPORTES = [
    // ─── Servicios ──────────────────────────────────────────────────────────
    {
        key: "estadisticas",
        seccion: "Servicios",
        titulo: "Estadísticas de servicios",
        descripcion: "Ingreso y cantidad por tipo, tiempos de respuesta, bajas y órdenes por ingeniero, por trimestre.",
        origen: "C-SE-01 · Estadísticas",
        icono: <BarChartOutlined />,
        endpoint: "reporteEstadisticasServiciosCMMS",
        filtros: ["rango", "cliente", "contrato", "conContrato", "ingeniero"],
        rangoInicial: esteAnio,
        nota: "Sólo servicios concluidos en el periodo. El tiempo de respuesta va de la solicitud del ticket al inicio del servicio (servicios que vienen de un ticket).",
        construir: (d) => {
            const r = d.resumen;
            const tipos = d.porTipo.map((x) => x.tipoServicio);
            const nombreTipo = (id) => d.porTipo.find((x) => x.tipoServicio === id)?.nombre || "";
            return {
                tiles: [
                    { titulo: "Servicios concluidos", valor: r.total, detalle: `${r.conContrato} con contrato · ${r.sinContrato} sin contrato` },
                    { titulo: "Ingreso", valor: formatoMoneda(r.ingresoTotal) || "$0.00", detalle: "Suma del monto de los servicios" },
                    {
                        titulo: "Tiempo de respuesta",
                        valor: r.respuesta ? `${r.respuesta.promedio} días` : "—",
                        detalle: r.respuesta ? `Máx. ${r.respuesta.maximo} · Mín. ${r.respuesta.minimo} · ${r.respuesta.muestras} con ticket` : "Sin servicios de ticket",
                    },
                    { titulo: "Duración promedio", valor: r.duracionPromedioMin ? minutosATexto(r.duracionPromedioMin) : "—", detalle: "Inicio a término del servicio" },
                    { titulo: "Bajas", valor: r.bajas, detalle: "Entregados con estatus Baja" },
                ],
                tablas: [
                    {
                        titulo: "Por tipo de servicio",
                        columnas: [t("Tipo de servicio", "texto", 28), t("Servicios", "numero", 12), t("% del total", "porcentaje", 12), t("Ingreso", "moneda", 16)],
                        filas: d.porTipo.map((x) => [x.nombre, x.n, x.porcentaje, x.ingreso]),
                        totales: ["Total", r.total, r.total ? 1 : null, r.ingresoTotal],
                    },
                    {
                        titulo: "Por estatus de entrega",
                        columnas: [t("Estatus de entrega", "texto", 26), t("Servicios", "numero", 12)],
                        filas: d.porEntrega.map((x) => [x.nombre, x.n]),
                    },
                    {
                        titulo: "Órdenes por ingeniero",
                        columnas: [t("Ingeniero", "texto", 32), t("Con contrato", "numero", 14), t("Sin contrato", "numero", 14), t("Total", "numero", 10), t("Ingreso", "moneda", 16)],
                        filas: d.porIngeniero.map((x) => [x.nombre, x.conContrato, x.sinContrato, x.total, x.ingreso]),
                        totales: ["Total", r.conContrato, r.sinContrato, r.total, r.ingresoTotal],
                    },
                    {
                        titulo: "Por trimestre",
                        columnas: [t("Trimestre", "texto", 12), ...tipos.map((x) => t(nombreTipo(x), "numero", 14)), t("Total", "numero", 10), t("% del periodo", "porcentaje", 12)],
                        filas: d.porTrimestre.map((q) => [q.trimestre, ...tipos.map((x) => q.porTipo[x] || 0), q.total, r.total ? q.total / r.total : null]),
                    },
                    {
                        titulo: "Rendimiento por ingeniero",
                        columnas: [t("Ingeniero", "texto", 32), ...d.trimestres.map((q) => t(q, "numero", 12)), t("Total", "numero", 10)],
                        filas: d.rendimiento.map((x) => [x.nombre, ...d.trimestres.map((q) => x.trimestres[q] || 0), x.total]),
                    },
                ],
            };
        },
    },
    {
        key: "bitacora",
        seccion: "Servicios",
        titulo: "Bitácora de órdenes de servicio",
        descripcion: "Todas las órdenes del periodo con su cliente, equipo, ingeniero, documentos de venta, monto y estado de pago.",
        origen: "C-SE-01 · Control",
        icono: <UnorderedListOutlined />,
        endpoint: "reporteBitacoraServiciosCMMS",
        filtros: ["rango", "estatusServicio", "cliente", "contrato", "conContrato", "ingeniero"],
        rangoInicial: esteMes,
        construir: (d) => ({
            tiles: [
                { titulo: "Órdenes", valor: d.servicios.length },
                { titulo: "Monto", valor: formatoMoneda(sum(d.servicios, (s) => s.monto)) || "$0.00" },
                { titulo: "Pagadas", valor: d.servicios.filter((s) => s.estadoPago === "Pagada").length, detalle: `${d.servicios.filter((s) => s.estadoPago === "Pendiente" || s.estadoPago === "Parcial").length} pendientes o parciales` },
            ],
            tablas: [
                {
                    titulo: "Órdenes de servicio",
                    nota: d.tope ? "Se muestran las primeras 5,000 órdenes: acorte el periodo para ver todas." : null,
                    columnas: [
                        t("Folio", "identificador", 11),
                        t("Folio externo", "identificador", 18),
                        t("Estatus", "texto", 12),
                        t("Tipo de servicio", "texto", 24),
                        t("Ticket", "identificador", 11),
                        t("Solicitud", "fecha", 12),
                        t("Inicio", "fechaHora", 17),
                        t("Conclusión", "fechaHora", 17),
                        t("Respuesta (días)", "numero", 12),
                        t("Cliente", "texto", 30),
                        t("Sucursal", "texto", 24),
                        t("Contrato", "texto", 20),
                        t("Equipo", "texto", 24),
                        t("Marca", "texto", 14),
                        t("Modelo", "texto", 14),
                        t("Serie", "identificador", 16),
                        t("Inventario", "identificador", 12),
                        t("Ingeniero", "texto", 26),
                        t("Estatus de entrega", "texto", 18),
                        t("Refacciones", "texto", 30),
                        t("Cotización", "texto", 16),
                        t("Pedido", "texto", 16),
                        t("Venta / factura", "texto", 18),
                        t("Monto", "moneda", 14),
                        t("Pago", "texto", 11),
                        t("Apto facturación", "texto", 12),
                        t("Notas", "texto", 30),
                    ],
                    filas: d.servicios.map((s) => [
                        s.codigo,
                        s.folioExterno,
                        s.estatusNombre,
                        s.tipoServicioNombre,
                        s.ticket,
                        s.fechaSolicitud,
                        s.fechaInicio,
                        s.fechaConclusion,
                        s.tiempoRespuestaDias,
                        s.clienteNombre,
                        s.sucursalNombre,
                        s.contratoNombre,
                        s.equipo,
                        s.marca,
                        s.modelo,
                        s.serie,
                        s.inventario,
                        s.ingeniero,
                        s.entregaNombre,
                        s.refacciones,
                        s.cotizacion,
                        s.pedido,
                        s.venta,
                        s.monto,
                        s.estadoPago,
                        s.aptoFacturacion,
                        s.notas,
                    ]),
                    totales: ["Total", ...Array(22).fill(null), sum(d.servicios, (s) => s.monto), null, null, null],
                },
            ],
        }),
    },
    {
        key: "refacciones",
        seccion: "Servicios",
        titulo: "Refacciones de los servicios",
        descripcion: "Refacciones instaladas bajo contrato y fuera de contrato, su costo, y las requeridas (hallazgos) pendientes de pedir.",
        origen: "Controles por cliente · Refacciones",
        icono: <ToolOutlined />,
        endpoint: "reporteRefaccionesServiciosCMMS",
        filtros: ["rango", "tipoRefaccion", "cliente", "contrato", "ingeniero"],
        rangoInicial: esteMes,
        construir: (d) => ({
            tiles: [
                { titulo: "Piezas instaladas", valor: sum(d.renglones, (p) => p.cantidad), detalle: `${d.porInsumo.length} refacciones distintas` },
                { titulo: "Costo", valor: formatoMoneda(sum(d.renglones, (p) => p.costoTotal)) || "$0.00", detalle: "Costo real si salió del almacén" },
                { titulo: "Fuera de contrato", valor: sum(d.renglones.filter((p) => p.fueraDeContrato), (p) => p.cantidad), detalle: "Piezas" },
                { titulo: "Requeridas sin pedir", valor: d.requeridas.filter((x) => !x.pedida).length, detalle: `${d.requeridas.length} hallazgos en el periodo` },
            ],
            tablas: [
                {
                    titulo: "Por refacción",
                    columnas: [t("Refacción", "texto", 40), t("Cantidad", "numero", 11), t("Bajo contrato", "numero", 13), t("Fuera de contrato", "numero", 15), t("Servicios", "numero", 11), t("Costo", "moneda", 15)],
                    filas: d.porInsumo.map((x) => [x.descripcion, x.cantidad, x.bajo, x.fuera, x.servicios, x.costoTotal]),
                    totales: ["Total", sum(d.porInsumo, (x) => x.cantidad), sum(d.porInsumo, (x) => x.bajo), sum(d.porInsumo, (x) => x.fuera), null, sum(d.porInsumo, (x) => x.costoTotal)],
                },
                {
                    titulo: "Detalle",
                    columnas: [
                        t("Conclusión", "fecha", 12),
                        t("Folio", "identificador", 11),
                        t("Folio externo", "identificador", 18),
                        t("Cliente", "texto", 28),
                        t("Sucursal", "texto", 22),
                        t("Contrato", "texto", 18),
                        t("Equipo", "texto", 30),
                        t("Refacción", "texto", 32),
                        t("Cantidad", "numero", 10),
                        t("Motivo", "texto", 24),
                        t("Contrato / fuera", "texto", 14),
                        t("Salió de almacén", "texto", 12),
                        t("Costo", "moneda", 14),
                    ],
                    filas: d.renglones.map((p) => [
                        p.fechaConclusion,
                        p.codigo,
                        p.folioExterno,
                        p.clienteNombre,
                        p.sucursalNombre,
                        p.contratoNombre,
                        p.activoTexto,
                        p.descripcion,
                        p.cantidad,
                        p.motivo,
                        p.fueraDeContrato ? "Fuera de contrato" : "Bajo contrato",
                        siNo(p.descontoInventario),
                        p.costoTotal,
                    ]),
                },
                {
                    titulo: "Refacciones requeridas (hallazgos)",
                    hoja: "Requeridas",
                    columnas: [t("Fecha", "fecha", 12), t("Folio", "identificador", 11), t("Cliente", "texto", 28), t("Sucursal", "texto", 22), t("Equipo", "texto", 30), t("Refacción requerida", "texto", 34), t("Pedida", "texto", 9)],
                    filas: d.requeridas.map((x) => [x.fechaInicio, x.codigo, x.clienteNombre, x.sucursalNombre, x.activoTexto, x.descripcion, siNo(x.pedida)]),
                },
            ],
        }),
    },
    {
        key: "viaticos",
        seccion: "Servicios",
        titulo: "Viáticos de los servicios",
        descripcion: "Hospedaje, alimentos, casetas... capturados en las Órdenes de Servicio, por categoría, ingeniero y cliente.",
        origen: "Orden de Servicio · Viáticos",
        icono: <WalletOutlined />,
        endpoint: "reporteViaticosServiciosCMMS",
        filtros: ["rango", "cliente", "contrato", "conContrato", "ingeniero"],
        rangoInicial: esteMes,
        nota: "Son informativos: no generan gasto ni póliza. Cuentan las órdenes en proceso y concluidas (por su fecha de conclusión o, si no ha concluido, de inicio). El ingeniero es el que se capturó en el viático o, si no, el de la orden.",
        construir: (d) => ({
            tiles: [
                { titulo: "Total de viáticos", valor: formatoMoneda(d.total) || "$0.00", detalle: `${d.renglones.length} viáticos` },
                { titulo: "Órdenes con viáticos", valor: new Set(d.renglones.map((v) => v.servicioId)).size },
                { titulo: "Categoría principal", valor: d.porCategoria[0]?.nombre || "—", detalle: d.porCategoria[0] ? formatoMoneda(d.porCategoria[0].monto) : "" },
            ],
            tablas: [
                {
                    titulo: "Por categoría",
                    columnas: [t("Categoría", "texto", 28), t("Viáticos", "numero", 11), t("Órdenes", "numero", 11), t("Monto", "moneda", 16), t("% del total", "porcentaje", 12)],
                    filas: d.porCategoria.map((x) => [x.nombre, x.renglones, x.servicios, x.monto, d.total ? x.monto / d.total : null]),
                    totales: ["Total", d.renglones.length, null, d.total, d.total ? 1 : null],
                },
                {
                    titulo: "Por ingeniero",
                    columnas: [t("Ingeniero", "texto", 32), t("Viáticos", "numero", 11), t("Órdenes", "numero", 11), t("Monto", "moneda", 16)],
                    filas: d.porIngeniero.map((x) => [x.nombre, x.renglones, x.servicios, x.monto]),
                    totales: ["Total", d.renglones.length, null, d.total],
                },
                {
                    titulo: "Por cliente",
                    columnas: [t("Cliente", "texto", 36), t("Viáticos", "numero", 11), t("Órdenes", "numero", 11), t("Monto", "moneda", 16)],
                    filas: d.porCliente.map((x) => [x.nombre, x.renglones, x.servicios, x.monto]),
                    totales: ["Total", d.renglones.length, null, d.total],
                },
                {
                    titulo: "Detalle",
                    columnas: [
                        t("Fecha", "fecha", 12),
                        t("Folio", "identificador", 11),
                        t("Folio externo", "identificador", 18),
                        t("Estatus", "texto", 12),
                        t("Tipo de servicio", "texto", 22),
                        t("Cliente", "texto", 28),
                        t("Sucursal", "texto", 22),
                        t("Contrato", "texto", 18),
                        t("Equipo", "texto", 30),
                        t("Ingeniero", "texto", 26),
                        t("Categoría", "texto", 16),
                        t("Descripción", "texto", 30),
                        t("Monto", "moneda", 14),
                    ],
                    filas: d.renglones.map((v) => [
                        v.fecha,
                        v.codigo,
                        v.folioExterno,
                        v.estatusNombre,
                        v.tipoServicioNombre,
                        v.clienteNombre,
                        v.sucursalNombre,
                        v.contratoNombre,
                        v.activoTexto,
                        v.empleadoNombre,
                        v.categoria,
                        v.descripcion,
                        v.monto,
                    ]),
                    // "Total" en la columna Folio: la primera es fecha y el Excel la formatea como tal.
                    totales: [null, "Total", ...Array(10).fill(null), d.total],
                },
            ],
        }),
    },
    {
        key: "aptoFacturacion",
        seccion: "Servicios",
        titulo: "Apto para facturación",
        descripcion: "Servicios concluidos por el mes en que son aptos para facturar, y los que aún no tienen mes asignado.",
        origen: "Control ISSEMYM · Apto para facturación",
        icono: <DollarOutlined />,
        endpoint: "reporteAptoFacturacionCMMS",
        filtros: ["anio", "cliente", "contrato"],
        nota: 'El mes se captura en el servicio (paso "Anexos y ventas"). "No" = concluidos del año sin mes asignado.',
        construir: (d) => ({
            tiles: [
                { titulo: "Aptos en el año", valor: sum(d.meses, (m) => m.n), detalle: formatoMoneda(sum(d.meses, (m) => m.monto)) },
                { titulo: "Sin mes asignado", valor: d.sinMarcar.n, detalle: formatoMoneda(d.sinMarcar.monto) },
                { titulo: "Ya ligados a una venta", valor: d.servicios.filter((s) => s.facturado).length },
            ],
            tablas: [
                {
                    titulo: "Por mes",
                    columnas: [t("Mes", "texto", 14), t("Servicios", "numero", 11), t("Monto", "moneda", 16)],
                    filas: [...d.meses.map((m) => [m.mes, m.n, m.monto]), ["No (sin mes)", d.sinMarcar.n, d.sinMarcar.monto]],
                    totales: ["Total", sum(d.meses, (m) => m.n) + d.sinMarcar.n, sum(d.meses, (m) => m.monto) + d.sinMarcar.monto],
                },
                {
                    titulo: "Servicios",
                    columnas: [
                        t("Apto en", "texto", 10),
                        t("Folio", "identificador", 11),
                        t("Folio externo", "identificador", 18),
                        t("Conclusión", "fecha", 12),
                        t("Tipo", "texto", 22),
                        t("Cliente", "texto", 28),
                        t("Sucursal", "texto", 22),
                        t("Contrato", "texto", 18),
                        t("Equipo", "texto", 30),
                        t("Monto", "moneda", 14),
                        t("Ligado a venta", "texto", 12),
                    ],
                    filas: d.servicios.map((s) => [s.mes, s.codigo, s.folioExterno, s.fechaConclusion, s.tipoServicioNombre, s.clienteNombre, s.sucursalNombre, s.contratoNombre, s.activoTexto, s.monto, siNo(s.facturado)]),
                },
            ],
        }),
    },
    {
        key: "visita",
        seccion: "Servicios",
        titulo: "Reporte de visita",
        descripcion: "Por día y sucursal: equipos atendidos, reportes atendidos e instalaciones, con su O.S. y estatus.",
        origen: "Control Santa Catarina · Reportes de Visita",
        icono: <EnvironmentOutlined />,
        endpoint: "reporteVisitaCMMS",
        filtros: ["rango", "cliente", "sucursal", "ingeniero"],
        requeridos: ["cliente"],
        rangoInicial: () => [moment().subtract(7, "days"), moment()],
        construir: (d) => ({
            tiles: [
                { titulo: "Visitas", valor: d.visitas, detalle: "Días con servicio por sucursal" },
                ...d.secciones.map((s) => ({ titulo: s, valor: d.renglones.filter((x) => x.seccion === s).length })),
            ],
            tablas: [
                {
                    titulo: "Reportes de visita",
                    columnas: [
                        t("Fecha", "fecha", 12),
                        t("Sucursal", "texto", 24),
                        t("Sección", "texto", 20),
                        t("Equipo", "texto", 24),
                        t("Marca", "texto", 14),
                        t("Modelo", "texto", 14),
                        t("Serie", "identificador", 16),
                        t("Área", "texto", 16),
                        t("O.S.", "identificador", 18),
                        t("Refacciones / comentarios", "texto", 36),
                        t("Estatus", "texto", 16),
                        t("Tipo de servicio", "texto", 22),
                        t("Ingeniero", "texto", 24),
                    ],
                    filas: d.renglones.map((x) => [`${x.fecha}T12:00:00Z`, x.sucursal, x.seccion, x.equipo, x.marca, x.modelo, x.serie, x.area, x.os, x.comentarios, x.estatus, x.tipoServicioNombre, x.ingeniero]),
                },
            ],
        }),
    },

    // ─── Contratos ──────────────────────────────────────────────────────────
    {
        key: "cumplimiento",
        seccion: "Contratos",
        titulo: "Cumplimiento mensual de contratos",
        descripcion: "Avance, faltante y motivo de cada contrato por mes, con promedios por bimestre, trimestre y cuatrimestre.",
        origen: "C-SE-03 · Reporte mensual",
        icono: <FileDoneOutlined />,
        endpoint: "reporteCumplimientoContratosCMMS",
        filtros: ["anio", "cliente", "contrato"],
        nota: "Cada mes cuenta lo calendarizado en ese mes y cuántos de ellos se realizaron (como el avance del Planeador). Lo que no tiene fecha se muestra aparte.",
        construir: (d) => {
            const prog = sum(d.contratos, (c) => c.total.programados);
            const real = sum(d.contratos, (c) => c.total.realizados);
            const detalle = [];
            d.contratos.forEach((c) =>
                c.meses.forEach((m) => {
                    if (m.programados) detalle.push([c.nombre, c.clienteNombre, m.mes, m.programados, m.realizados, m.noRealizados, m.pendientes, m.avance, m.avance === null ? null : 1 - m.avance, m.motivos]);
                })
            );
            return {
                tiles: [
                    { titulo: "Contratos", valor: d.contratos.length, detalle: `Año ${d.anio}` },
                    { titulo: "Avance del año", valor: prog ? formatoPorcentaje(real / prog) : "—", detalle: `${real} de ${prog} calendarizados` },
                    { titulo: "Sin fecha", valor: sum(d.contratos, (c) => c.sinFecha), detalle: "Pendientes por calendarizar" },
                ],
                tablas: [
                    {
                        titulo: "Avance por mes",
                        columnas: [t("Contrato", "texto", 22), t("Cliente", "texto", 28), ...MESES.map((m) => t(m, "porcentaje", 8)), t("Promedio", "porcentaje", 10), t("Programados", "numero", 12), t("Realizados", "numero", 11), t("Sin fecha", "numero", 10)],
                        filas: d.contratos.map((c) => [c.nombre, c.clienteNombre, ...c.meses.map((m) => m.avance), c.promedios.anual, c.total.programados, c.total.realizados, c.sinFecha]),
                    },
                    {
                        titulo: "Detalle mensual con motivo",
                        hoja: "Detalle mensual",
                        columnas: [
                            t("Contrato", "texto", 22),
                            t("Cliente", "texto", 28),
                            t("Mes", "texto", 8),
                            t("Programados", "numero", 12),
                            t("Realizados", "numero", 11),
                            t("No realizados", "numero", 13),
                            t("Pendientes", "numero", 11),
                            t("Avance", "porcentaje", 10),
                            t("Faltante", "porcentaje", 10),
                            t("Motivo", "texto", 40),
                        ],
                        filas: detalle,
                    },
                    {
                        titulo: "Promedios por periodo",
                        hoja: "Promedios",
                        columnas: [
                            t("Contrato", "texto", 22),
                            ...[1, 2, 3, 4, 5, 6].map((n) => t(`Bim ${n}`, "porcentaje", 9)),
                            ...[1, 2, 3, 4].map((n) => t(`Trim ${n}`, "porcentaje", 9)),
                            ...[1, 2, 3].map((n) => t(`Cuat ${n}`, "porcentaje", 9)),
                            t("Anual", "porcentaje", 9),
                        ],
                        filas: d.contratos.map((c) => [c.nombre, ...c.promedios.bimestres, ...c.promedios.trimestres, ...c.promedios.cuatrimestres, c.promedios.anual]),
                    },
                ],
            };
        },
    },
    {
        key: "porContrato",
        seccion: "Contratos",
        titulo: "Servicios por contrato e ingeniero",
        descripcion: "Cuántos preventivos, correctivos, diagnósticos y bajas tuvo cada contrato, y cuántas órdenes hizo cada ingeniero en cada uno.",
        origen: "C-SE-03 · Tipo de servicio por contrato · OS por ingeniero",
        icono: <TeamOutlined />,
        endpoint: "reporteServiciosPorContratoCMMS",
        filtros: ["rango", "cliente", "contrato"],
        rangoInicial: esteAnio,
        construir: (d) => ({
            tiles: [
                { titulo: "Servicios", valor: sum(d.contratos, (c) => c.total) },
                { titulo: "Contratos", valor: d.contratos.filter((c) => c.clave !== "sin").length },
                { titulo: "Ingenieros", valor: d.ingenieros.length },
            ],
            tablas: [
                {
                    titulo: "Por tipo de servicio",
                    columnas: [t("Contrato", "texto", 24), t("Cliente", "texto", 28), ...d.tiposPresentes.map((x) => t(x.nombre, "numero", 14)), t("Total", "numero", 10), t("Bajas", "numero", 9), t("Monto", "moneda", 15)],
                    filas: d.contratos.map((c) => [c.nombre, c.clienteNombre, ...d.tiposPresentes.map((x) => c.porTipo[x.tipoServicio] || 0), c.total, c.bajas, c.monto]),
                    totales: ["Total", null, ...d.tiposPresentes.map((x) => sum(d.contratos, (c) => c.porTipo[x.tipoServicio])), sum(d.contratos, (c) => c.total), sum(d.contratos, (c) => c.bajas), sum(d.contratos, (c) => c.monto)],
                },
                {
                    titulo: "Órdenes por ingeniero",
                    columnas: [t("Ingeniero", "texto", 30), ...d.columnasContrato.map((c) => t(c.nombre, "numero", 14)), t("Total", "numero", 10)],
                    filas: d.ingenieros.map((i) => [i.nombre, ...d.columnasContrato.map((c) => i.porContrato[c.clave] || 0), i.total]),
                    totales: ["Total", ...d.columnasContrato.map((c) => sum(d.ingenieros, (i) => i.porContrato[c.clave])), sum(d.ingenieros, (i) => i.total)],
                },
            ],
        }),
    },
    {
        key: "facturacion",
        seccion: "Contratos",
        titulo: "Facturación de contratos",
        descripcion: "Facturado por mes de cada contrato (sus ventas ligadas), cobrado, por cobrar y porcentaje cobrado del valor.",
        origen: "C-SE-03 · Financiero",
        icono: <DollarOutlined />,
        endpoint: "reporteFacturacionContratosCMMS",
        filtros: ["anio", "cliente", "contrato"],
        nota: 'Usa las ventas ligadas en cada contrato (paso "Facturación"). El mes es el periodo capturado al ligarla o, si no tiene, la fecha de la venta.',
        construir: (d) => ({
            tiles: [
                { titulo: "Facturado", valor: formatoMoneda(sum(d.contratos, (c) => c.facturado)) || "$0.00", detalle: `Año ${d.anio}` },
                { titulo: "Cobrado", valor: formatoMoneda(sum(d.contratos, (c) => c.cobrado)) || "$0.00" },
                { titulo: "Por cobrar", valor: formatoMoneda(sum(d.contratos, (c) => c.porCobrar)) || "$0.00" },
            ],
            tablas: [
                {
                    titulo: "Facturado por mes",
                    columnas: [
                        t("Contrato", "texto", 22),
                        t("Cliente", "texto", 28),
                        t("Valor", "moneda", 15),
                        ...MESES.map((m) => t(m, "moneda", 13)),
                        t("Facturado", "moneda", 15),
                        t("Cobrado", "moneda", 15),
                        t("Por cobrar", "moneda", 15),
                        t("Cobrado del valor", "porcentaje", 12),
                    ],
                    filas: d.contratos.map((c) => [c.nombre, c.clienteNombre, c.valorVariable ? null : c.valor, ...c.meses, c.facturado, c.cobrado, c.porCobrar, c.porcentajeCobrado]),
                    totales: ["Total", null, null, ...MESES.map((_, i) => sum(d.contratos, (c) => c.meses[i])), sum(d.contratos, (c) => c.facturado), sum(d.contratos, (c) => c.cobrado), sum(d.contratos, (c) => c.porCobrar), null],
                },
                {
                    titulo: "Ventas ligadas",
                    hoja: "Ventas",
                    columnas: [t("Contrato", "texto", 22), t("Cliente", "texto", 28), t("Venta / factura", "texto", 22), t("Periodo", "texto", 10), t("Total", "moneda", 15), t("Cobrado", "moneda", 15)],
                    filas: d.contratos.flatMap((c) => c.facturas.map((f) => [c.nombre, c.clienteNombre, f.texto, f.periodo, f.total, f.pagado])),
                },
            ],
        }),
    },
    {
        key: "revRepIns",
        seccion: "Contratos",
        titulo: "Revisiones, reparaciones e instalaciones",
        descripcion: "Por unidad médica y quincena: revisiones (REV), reparaciones (REP) e instalaciones (INS), y las refacciones bajo contrato.",
        origen: "Control ISSEMYM · Resumen REV / REP / INS",
        icono: <TableOutlined />,
        endpoint: "reporteRevRepInsCMMS",
        filtros: ["rango", "cliente", "contrato"],
        requeridos: ["clienteOContrato"],
        rangoInicial: () => [moment().subtract(2, "months").startOf("month"), moment()],
        construir: (d) => {
            const g = ["REV", "REP", "INS"];
            const totalUnidad = (u, k) => sum(d.quincenas, (q) => u.celdas[q.clave]?.[k]);
            const refUnidad = (u, k) => sum(d.quincenas, (q) => u.refacciones[q.clave]?.[k]);
            return {
                tiles: g.map((k) => ({ titulo: k, valor: sum(d.unidades, (u) => totalUnidad(u, k)), detalle: (d.grupos[k] || []).join(", ") })),
                tablas: [
                    {
                        titulo: "Servicios por unidad y quincena",
                        hoja: "REV REP INS",
                        columnas: [t("Unidad", "texto", 36), ...d.quincenas.flatMap((q) => g.map((k) => t(`${q.etiqueta} ${k}`, "numero", 9))), ...g.map((k) => t(`Total ${k}`, "numero", 10))],
                        filas: d.unidades.map((u) => [u.unidad, ...d.quincenas.flatMap((q) => g.map((k) => u.celdas[q.clave]?.[k] || 0)), ...g.map((k) => totalUnidad(u, k))]),
                        totales: ["Total por quincena", ...d.quincenas.flatMap((q) => g.map((k) => sum(d.unidades, (u) => u.celdas[q.clave]?.[k]))), ...g.map((k) => sum(d.unidades, (u) => totalUnidad(u, k)))],
                    },
                    {
                        titulo: "Refacciones bajo contrato (piezas)",
                        hoja: "Refacciones",
                        columnas: [t("Unidad", "texto", 36), ...d.quincenas.map((q) => t(q.etiqueta, "numero", 11)), t("Bajo contrato", "numero", 13), t("Fuera de contrato", "numero", 15)],
                        filas: d.unidades.map((u) => [u.unidad, ...d.quincenas.map((q) => u.refacciones[q.clave]?.bajo || 0), refUnidad(u, "bajo"), refUnidad(u, "fuera")]),
                        totales: ["Total", ...d.quincenas.map((q) => sum(d.unidades, (u) => u.refacciones[q.clave]?.bajo)), sum(d.unidades, (u) => refUnidad(u, "bajo")), sum(d.unidades, (u) => refUnidad(u, "fuera"))],
                    },
                ],
            };
        },
    },
    {
        key: "materiales",
        seccion: "Contratos",
        titulo: "Materiales para el programa",
        descripcion: "Refacciones necesarias para los preventivos del contrato: kit del tipo de activo × servicios que faltan de cada equipo.",
        origen: "Control ISSEMYM · Lista de Materiales",
        icono: <ExperimentOutlined />,
        endpoint: "reporteMaterialesProgramaCMMS",
        filtros: ["contrato", "soloPendientes"],
        requeridos: ["contrato"],
        nota: "El kit se define en Tipos de Activo › Kit de refacciones. Los equipos cuyo tipo no tiene kit aparecen marcados.",
        construir: (d) => ({
            tiles: [
                { titulo: "Equipos del contrato", valor: d.equipos },
                { titulo: "Refacciones distintas", valor: d.consolidado.length },
                { titulo: "Modelos sin kit", valor: d.renglones.filter((r) => r.sinKit).length, detalle: "Defina el kit en su tipo de activo" },
            ],
            tablas: [
                {
                    titulo: "Consolidado",
                    columnas: [t("Refacción", "texto", 44), t("Total requerido", "numero", 15), t("Modelos que la usan", "numero", 16)],
                    filas: d.consolidado.map((c) => [c.insumo, c.total, c.modelos]),
                },
                {
                    titulo: "Por tipo y modelo",
                    hoja: "Por modelo",
                    columnas: [t("Tipo de activo", "texto", 26), t("Marca", "texto", 14), t("Modelo", "texto", 16), t("Equipos", "numero", 9), t(d.soloPendientes ? "Preventivos pendientes" : "Preventivos", "numero", 14), t("Refacción", "texto", 40), t("Por servicio", "numero", 11), t("Total", "numero", 10)],
                    filas: d.renglones.map((r) => [r.tipo, r.marca, r.modelo, r.equipos, r.servicios, r.sinKit ? "Sin kit en el tipo de activo" : r.insumo, r.porServicio, r.total]),
                },
            ],
        }),
    },

    // ─── Equipos ────────────────────────────────────────────────────────────
    {
        key: "estadoEquipos",
        seccion: "Equipos",
        titulo: "Estado de los equipos",
        descripcion: "Cuántos equipos están funcionales, condicionados, dañados o de baja, por sucursal, con su último y próximo preventivo.",
        origen: "Control Santa Catarina · Dashboard",
        icono: <MedicineBoxOutlined />,
        endpoint: "reporteEstadoEquiposCMMS",
        filtros: ["cliente", "contrato", "sucursal"],
        construir: (d) => ({
            tiles: [
                { titulo: "Equipos", valor: d.total, detalle: `${d.preventivosVencidos} con preventivo vencido` },
                ...d.porEstatus.map((e) => ({ titulo: e.nombre, valor: e.n, detalle: formatoPorcentaje(e.porcentaje) })),
            ],
            tablas: [
                {
                    titulo: "Por sucursal",
                    columnas: [t("Sucursal", "texto", 36), t("Funcional", "numero", 11), t("Funcional condicionado", "numero", 14), t("Dañado", "numero", 10), t("Baja", "numero", 9), t("Total", "numero", 9)],
                    filas: d.porSucursal.map((s) => [s.sucursal, s[1] || 0, s[2] || 0, s[3] || 0, s[4] || 0, s.total]),
                    totales: ["Total", ...[1, 2, 3, 4].map((e) => sum(d.porSucursal, (s) => s[e])), d.total],
                },
                {
                    titulo: "Equipos",
                    columnas: [
                        t("Folio", "identificador", 11),
                        t("Tipo", "texto", 24),
                        t("Marca", "texto", 14),
                        t("Modelo", "texto", 14),
                        t("Serie", "identificador", 16),
                        t("Inventario", "identificador", 12),
                        t("Cliente", "texto", 26),
                        t("Sucursal", "texto", 22),
                        t("Ubicación", "texto", 16),
                        t("Área", "texto", 14),
                        t("Contrato", "texto", 18),
                        t("Estatus", "texto", 16),
                        t("Último preventivo", "fecha", 13),
                        t("Próximo preventivo", "fecha", 13),
                    ],
                    filas: d.equipos.map((a) => [a.codigo, a.tipoNombre, a.marcaNombre, a.modelo, a.serie, a.numeroInventario, a.clienteNombre, a.sucursalNombre, a.ubicacion, a.area, a.contratoNombre, a.estatusNombre, a.fechaUltimoPreventivo, a.fechaProximoPreventivo]),
                },
            ],
        }),
    },
    {
        key: "seguimiento",
        seccion: "Equipos",
        titulo: "Seguimiento de reportes",
        descripcion: "Fallas reportadas (tickets): fecha de reporte, estatus, cierre, días de atención, O.S. e ingeniero.",
        origen: "Control Santa Catarina · Seguimiento a equipos retirados",
        icono: <AlertOutlined />,
        endpoint: "reporteSeguimientoReportesCMMS",
        filtros: ["rango", "cliente", "sucursal", "soloAbiertos"],
        rangoInicial: () => [moment().subtract(3, "months"), moment()],
        nota: "Días de atención: del reporte al cierre; si sigue abierto, hasta hoy. Los cancelados no cuentan.",
        construir: (d) => ({
            tiles: [
                { titulo: "Reportes", valor: d.resumen.total, detalle: d.resumen.cancelados ? `${d.resumen.cancelados} cancelados` : null },
                { titulo: "Abiertos", valor: d.resumen.abiertos },
                { titulo: "Atendidos", valor: d.resumen.cerrados, detalle: "Finalizados o cerrados" },
                { titulo: "Días promedio al cierre", valor: d.resumen.promedioDiasCierre ?? "—", detalle: "Sólo los atendidos" },
            ],
            tablas: [
                {
                    titulo: "Reportes",
                    columnas: [
                        t("Ticket", "identificador", 11),
                        t("Equipo", "texto", 24),
                        t("Marca", "texto", 14),
                        t("Modelo", "texto", 14),
                        t("Serie", "identificador", 16),
                        t("Sucursal", "texto", 22),
                        t("Ubicación", "texto", 18),
                        t("Reporte", "texto", 40),
                        t("Fecha de reporte", "fechaHora", 17),
                        t("Estatus", "texto", 20),
                        t("Cierre", "fechaHora", 17),
                        t("Días de atención", "numero", 11),
                        t("O.S.", "texto", 18),
                        t("Ingeniero", "texto", 24),
                        t("Comentarios", "texto", 36),
                        t("Estatus del equipo", "texto", 18),
                    ],
                    filas: d.tickets.map((x) => [x.codigo, x.equipo, x.marca, x.modelo, x.serie, x.sucursal, x.ubicacion, x.reporte, x.fechaReporte, x.estatusNombre, x.fechaCierre, x.diasAtencion, x.os, x.ingeniero, x.comentarios, x.estatusEquipo]),
                },
            ],
        }),
    },

    // ─── Viajes ─────────────────────────────────────────────────────────────
    {
        key: "viajes",
        seccion: "Viajes",
        titulo: "Km y días por ingeniero",
        descripcion: "Resumen semanal de viajes: destinos, días y kilómetros por ingeniero, y el detalle de cada viaje.",
        origen: "C-LO-01 · Resumen semanal",
        icono: <CarOutlined />,
        endpoint: "reporteViajesCMMS",
        filtros: ["rango", "ingeniero"],
        rangoInicial: esteAnio,
        nota: "Cada ingeniero suma los km completos de los viajes en que fue; los km de un viaje cuentan en la semana en que sale. Días = días de viaje dentro del periodo.",
        construir: (d) => ({
            tiles: [
                { titulo: "Viajes", valor: d.totales.viajes },
                { titulo: "Días de viaje", valor: d.totales.dias, detalle: "Suma de los días de cada viaje" },
                { titulo: "Km recorridos", valor: d.totales.km.toLocaleString("es-MX"), detalle: "Ida y vuelta de cada viaje" },
                { titulo: "Ingenieros", valor: d.porIngeniero.length, detalle: "Que viajaron en el periodo" },
            ],
            tablas: [
                {
                    titulo: "Km y días por ingeniero",
                    columnas: [t("Ingeniero", "texto", 32), t("Viajes", "numero", 10), t("Días", "numero", 10), t("Km", "numero", 12), t("Destinos", "texto", 50)],
                    filas: d.porIngeniero.map((x) => [x.nombre, x.viajes, x.dias, x.km, x.destinos.join(", ")]),
                    totales: ["Total", sum(d.porIngeniero, (x) => x.viajes), sum(d.porIngeniero, (x) => x.dias), sum(d.porIngeniero, (x) => x.km), null],
                },
                {
                    titulo: "Resumen semanal",
                    columnas: [t("Semana", "texto", 26), t("Ingeniero", "texto", 30), t("Destinos", "texto", 44), t("Viajes", "numero", 10), t("Días", "numero", 10), t("Km", "numero", 12)],
                    filas: d.semanas.flatMap((s) => s.filas.map((x) => [s.etiqueta, x.nombre, x.destinos.join(", "), x.viajes, x.dias, x.km])),
                },
                {
                    titulo: "Viajes",
                    columnas: [
                        t("Folio", "identificador", 11),
                        t("Salida", "fecha", 12),
                        t("Regreso", "fecha", 12),
                        t("Días", "numero", 8),
                        t("Destino", "texto", 22),
                        t("Unidades", "texto", 34),
                        t("Vehículo", "texto", 20),
                        t("Personal", "texto", 36),
                        t("Km", "numero", 10),
                        t("Cancela comedor", "texto", 10),
                        t("Notas", "texto", 30),
                    ],
                    filas: d.viajes.map((v) => [v.codigo, v.fechaInicio, v.fechaFin, v.dias, v.destino, v.unidades, v.vehiculo, v.personal, v.km, siNo(v.cancelaComedor), v.notas]),
                    totales: ["Total", null, null, sum(d.viajes, (v) => v.dias), null, null, null, null, sum(d.viajes, (v) => v.km), null, null],
                },
            ],
        }),
    },
];

export const reportePorClave = (key) => REPORTES.find((r) => r.key === key) || null;
